import type { LogDescriptor } from '../core/log/client';
import { verifyInclusion } from '../core/merkle/proof';
import { leafIndexFromExtensions } from '../core/rfc6962/sct';
import type { Sct, SignedEntry } from '../core/rfc6962/types';
import { leafForSct } from '../core/sct/embedded';
import { TransportError } from '../core/transport/errors';
import { getClient } from './state/clients';
import { registry } from './state/logs';
import { refreshTreeHead } from './state/treeHeads';

/** Outcome of asking "is the (pre)certificate behind this SCT really in that log right now?" */
export type InclusionCheck =
  /** Inclusion proof fetched and verified against the log's current signed tree head. */
  | { status: 'included'; log: LogDescriptor; index: number; treeSize: number; headVerified: boolean; leafMatched: boolean | 'unknown' }
  /** The log answered, but the proof does not check out (or the entry at that index is a different leaf). */
  | { status: 'invalid'; log: LogDescriptor; index?: number; treeSize: number; detail: string }
  /** The log has no such leaf (yet): 404/400 on the hash lookup. */
  | { status: 'not-found'; log: LogDescriptor; withinMmd: boolean }
  | { status: 'blocked'; log: LogDescriptor; error: TransportError }
  | { status: 'unknown-log' }
  /** The leaf cannot be reconstructed (needs the issuer) or the tiled SCT lacks leaf_index. */
  | { status: 'no-entry'; log: LogDescriptor; reason: string }
  | { status: 'error'; log: LogDescriptor; error: unknown }
  /** The log did not answer within the time limit. */
  | { status: 'timeout'; log: LogDescriptor };

const cache = new Map<string, Promise<InclusionCheck>>();

/**
 * Locates the entry an SCT was issued for and verifies its inclusion proof against the log's
 * signed head. RFC 6962 logs are asked by leaf hash (so `entry` — the reconstructed precert
 * signed_entry or the x509 cert — is required); tiled logs by the SCT's leaf_index extension, and
 * the leaf hash from the tiles is compared with the one derived from `entry` when it is known.
 */
export function checkInclusion(sct: Sct, entry: SignedEntry | undefined, force = false): Promise<InclusionCheck> {
  const log = registry.value.byLogId(sct.logId);
  if (!log) return Promise.resolve({ status: 'unknown-log' });
  const key = `${log.id}|${sct.timestamp}|${sct.signature.signature.toHex().slice(0, 32)}|${entry ? 'e' : '-'}`;
  const cached = cache.get(key);
  if (cached && !force) return cached;
  const p = run(log, sct, entry);
  cache.set(key, p);
  return p;
}

const CHECK_TIMEOUT_MS = 45_000;

async function run(log: LogDescriptor, sct: Sct, entry: SignedEntry | undefined): Promise<InclusionCheck> {
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), CHECK_TIMEOUT_MS);
  try {
    return await Promise.race([
      runWith(log, sct, entry, ac.signal),
      new Promise<InclusionCheck>((resolve) => ac.signal.addEventListener('abort', () => resolve({ status: 'timeout', log }))),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

async function runWith(log: LogDescriptor, sct: Sct, entry: SignedEntry | undefined, signal: AbortSignal): Promise<InclusionCheck> {
  try {
    const client = getClient(log);
    const idx = leafIndexFromExtensions(sct.extensions);
    const expected = entry ? await leafForSct(sct, entry) : undefined;
    const head = await refreshTreeHead(client);
    const headVerified = head.signature.status === 'verified';
    if (client.supportsHashLookup) {
      if (!expected) return { status: 'no-entry', log, reason: 'the leaf hash needs the issuer certificate' };
      const proof = await client.getInclusionProof({ leafHash: expected.hash }, head.treeSize, signal);
      const r = await verifyInclusion(expected.hash, proof.leafIndex, head.treeSize, proof.auditPath, head.rootHash);
      return r.ok ? { status: 'included', log, index: proof.leafIndex, treeSize: head.treeSize, headVerified, leafMatched: true } : { status: 'invalid', log, index: proof.leafIndex, treeSize: head.treeSize, detail: r.error ?? 'computed root differs from the signed head' };
    }
    if (idx === undefined) return { status: 'no-entry', log, reason: 'the SCT carries no leaf_index extension' };
    if (idx >= head.treeSize) return { status: 'not-found', log, withinMmd: withinMmd(log, sct) };
    const proof = await client.getInclusionProof({ index: idx }, head.treeSize, signal);
    const leafHash = proof.leafHash ?? expected?.hash;
    if (!leafHash) return { status: 'no-entry', log, reason: 'no leaf hash available for the entry' };
    if (expected && proof.leafHash && expected.hash.toHex() !== proof.leafHash.toHex()) {
      return { status: 'invalid', log, index: idx, treeSize: head.treeSize, detail: `entry #${idx} is a different leaf than this certificate` };
    }
    const r = await verifyInclusion(leafHash, idx, head.treeSize, proof.auditPath, head.rootHash);
    return r.ok ? { status: 'included', log, index: idx, treeSize: head.treeSize, headVerified, leafMatched: expected ? true : 'unknown' } : { status: 'invalid', log, index: idx, treeSize: head.treeSize, detail: r.error ?? 'computed root differs from the signed head' };
  } catch (error) {
    if (error instanceof TransportError) {
      if (error.kind === 'cors-blocked') return { status: 'blocked', log, error };
      if (error.kind === 'http' && (error.status === 404 || error.status === 400)) return { status: 'not-found', log, withinMmd: withinMmd(log, sct) };
    }
    return { status: 'error', log, error };
  }
}

function withinMmd(log: LogDescriptor, sct: Sct): boolean {
  return Date.now() - sct.timestamp < (log.mmd ?? 86_400) * 1000;
}

export interface InclusionSummary {
  included: number;
  total: number;
  pending: number;
  problems: number;
}

export function summarize(checks: Array<InclusionCheck | 'running' | undefined>): InclusionSummary {
  let included = 0, pending = 0, problems = 0;
  for (const c of checks) {
    if (c === undefined || c === 'running') pending++;
    else if (c.status === 'included') included++;
    else if (c.status === 'timeout') pending++;
    else if (c.status === 'not-found' && c.withinMmd) pending++;
    else problems++;
  }
  return { included, total: checks.length, pending, problems };
}
